import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  Breadcrumbs,
  Link,
  Alert,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  TextField,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FolderIcon from '@mui/icons-material/Folder';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TuneIcon from '@mui/icons-material/Tune';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import EventIcon from '@mui/icons-material/Event';

// Contexts and Components
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeaderCard } from '../../components/common';

// Services
import { usersAPI, projectsAPI } from '../../services/api';

// Utils and Constants
import {
  PROJECT_STATUS,
  PROJECT_PRIORITY,
  PROJECT_CATEGORY,
  PROJECT_CATEGORY_OPTIONS,
  PROJECT_STATUS_OPTIONS,
  PROJECT_PRIORITY_OPTIONS,
} from '../../constants/projectEnums';

const getInitials = (name) => {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase();
};

const STEPS = [
  { key: 'basic', label: 'Details', icon: EditNoteIcon },
  { key: 'timeline', label: 'Timeline', icon: CalendarMonthIcon },
  { key: 'manager', label: 'Manager', icon: PersonSearchIcon },
  { key: 'settings', label: 'Settings', icon: TuneIcon },
];

const initialFormState = {
  name: '',
  description: '',
  category: PROJECT_CATEGORY.DEVELOPMENT,
  startDate: '',
  dueDate: '',
  status: PROJECT_STATUS.NOT_STARTED,
  priority: PROJECT_PRIORITY.MEDIUM,
  managerId: '',
};

const ProjectCreate = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getActiveCompany } = useAuth();
  const { showSuccess } = useNotification();
  const { refreshProjects } = useProject();
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const [managerFetchError, setManagerFetchError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
  const activeCompanyId = activeCompany?.id || null;

  useEffect(() => {
    const fetchManagers = async () => {
      if (!activeCompanyId) {
        setAvailableManagers([]);
        return;
      }

      setIsLoadingManagers(true);
      setManagerFetchError('');

      try {
        const response = await usersAPI.listByCompany(activeCompanyId, {
          includeSelf: true,
          page: 1,
          pageSize: 100,
          sortBy: 'name',
          sortOrder: 'asc',
        });
        const users = response.items || [];
        setAvailableManagers(
          users.map((member) => ({
            ...member,
            name: member.name || member.email,
            department: member.role ? member.role.charAt(0).toUpperCase() + member.role.slice(1) : 'User',
          }))
        );
      } catch (error) {
        setManagerFetchError(error.message || 'Failed to load managers');
        setAvailableManagers([]);
      } finally {
        setIsLoadingManagers(false);
      }
    };

    fetchManagers();
  }, [activeCompanyId]);

  const selectedManager = useMemo(() => {
    return availableManagers.find((m) => m.id === formData.managerId);
  }, [formData.managerId, availableManagers]);

  const projectDuration = useMemo(() => {
    if (formData.startDate && formData.dueDate) {
      const start = dayjs(formData.startDate);
      const end = dayjs(formData.dueDate);

      if (!start.isValid() || !end.isValid()) return null;

      const totalDays = end.diff(start, 'day');
      if (totalDays < 0) return null;
      if (totalDays === 0) return '1 day';

      const pluralize = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

      const months = end.diff(start, 'month');
      if (months > 0) {
        const daysAfterMonths = end.diff(start.add(months, 'month'), 'day');
        return daysAfterMonths > 0
          ? `${pluralize(months, 'month')} ${pluralize(daysAfterMonths, 'day')}`
          : pluralize(months, 'month');
      }

      const weeks = Math.floor(totalDays / 7);
      const days = totalDays % 7;
      if (weeks > 0) {
        return days > 0
          ? `${pluralize(weeks, 'week')} ${pluralize(days, 'day')}`
          : pluralize(weeks, 'week');
      }

      return pluralize(totalDays, 'day');
    }
    return null;
  }, [formData.startDate, formData.dueDate]);

  const validateCurrentStep = () => {
    const newErrors = {};

    if (currentStep === 0) {
      if (!formData.name.trim()) {
        newErrors.name = 'Project name is required';
      } else if (formData.name.trim().length < 3) {
        newErrors.name = 'Minimum 3 characters';
      }
      if (!formData.description.trim()) {
        newErrors.description = 'Description is required';
      }
    }

    if (currentStep === 1) {
      if (!formData.startDate) {
        newErrors.startDate = 'Start date is required';
      }
      if (!formData.dueDate) {
        newErrors.dueDate = 'Due date is required';
      } else if (formData.startDate && formData.dueDate < formData.startDate) {
        newErrors.dueDate = 'Must be after start date';
      }
    }

    if (currentStep === 2) {
      if (!formData.managerId) {
        newErrors.managerId = 'Please select a manager';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleManagerSelect = (managerId) => {
    setFormData((prev) => ({ ...prev, managerId }));
    if (errors.managerId) {
      setErrors((prev) => ({ ...prev, managerId: '' }));
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    if (!activeCompanyId) {
      setSubmitError('No active company selected. Please select a company first.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const newProject = await projectsAPI.create({
        companyId: activeCompanyId,
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        startDate: formData.startDate,
        dueDate: formData.dueDate,
        managerId: formData.managerId,
        priority: formData.priority,
        status: formData.status,
      });

      try {
        await refreshProjects();
      } catch (refreshError) {
        console.warn('Failed to refresh project list after creation:', refreshError);
      }

      showSuccess('Project added successfully');
      navigate(`/projects/${newProject.id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      setSubmitError(error.message || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/projects');
  };

  const completionPercent = Math.round(((currentStep + 1) / STEPS.length) * 100);

  // Custom Step Icon Component
  const CustomStepIcon = ({ active, completed, icon }) => {
    const StepIconComponent = STEPS[icon - 1]?.icon;
    return (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.100',
          color: completed || active ? 'white' : 'grey.400',
          border: 2,
          borderColor: completed ? 'success.main' : active ? 'primary.main' : 'grey.200',
        }}
      >
        {completed ? (
          <CheckCircleIcon sx={{ fontSize: 22 }} />
        ) : (
          StepIconComponent && <StepIconComponent sx={{ fontSize: 20 }} />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1080, mx: 'auto', pb: 4 }}>
      {/* Header */}
      <Box>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleCancel}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <FolderIcon fontSize="small" />
            Projects
          </Link>
          <Typography color="text.primary" variant="body2">
            Create New Project
          </Typography>
        </Breadcrumbs>

        <PageHeaderCard
          mb={0}
          title="Create New Project"
          subtitle="Fill in the details to create a new project"
          titleVariant={isMobile ? 'h5' : 'h4'}
          icon={<AddIcon sx={{ fontSize: { xs: 22, sm: 26 }, color: 'primary.main' }} />}
          actions={(
            <Box sx={{ width: { xs: '100%', md: 280 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Step {currentStep + 1} of {STEPS.length}
                </Typography>
                <Typography variant="caption" color="primary.main" fontWeight={700}>
                  {completionPercent}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercent}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                }}
              />
            </Box>
          )}
        />
      </Box>

      {isSubmitting && (
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: 99,
            height: 6,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          }}
        />
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {submitError}
        </Alert>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          border: 1,
          borderColor: alpha(theme.palette.primary.main, 0.12),
          boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.06)}`,
        }}
      >
        <Stepper
          activeStep={currentStep}
          alternativeLabel
          sx={{
            mb: { xs: 2.5, sm: 4 },
            '& .MuiStepLabel-label': {
              mt: 1,
              fontWeight: 600,
            },
            '& .MuiStepConnector-line': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
              borderTopWidth: 2,
            },
          }}
        >
          {STEPS.map((step) => (
            <Step key={step.key}>
              <StepLabel StepIconComponent={CustomStepIcon}>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Basic Information */}
        {currentStep === 0 && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h6" fontWeight={600}>
                Basic Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                What is your project about?
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Project Name"
                placeholder="e.g. Website Redesign"
                value={formData.name}
                onChange={handleChange('name')}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />

              <TextField
                fullWidth
                label="Description"
                placeholder="Describe the project objectives and scope..."
                value={formData.description}
                onChange={handleChange('description')}
                error={Boolean(errors.description)}
                helperText={errors.description}
                multiline
                rows={4}
                InputLabelProps={{
                  sx: {
                    bgcolor: 'background.paper',
                    px: 0.5,
                  },
                }}
              />

              {/* Category Selection */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Project Category
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {PROJECT_CATEGORY_OPTIONS.map((cat) => (
                    <Chip
                      key={cat.value}
                      label={cat.label}
                      icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setFormData((prev) => ({ ...prev, category: cat.value }))}
                      sx={{
                        height: 36,
                        px: 0.5,
                        bgcolor: formData.category === cat.value ? `${cat.color}15` : 'transparent',
                        borderColor: formData.category === cat.value ? cat.color : 'grey.300',
                        color: formData.category === cat.value ? cat.color : 'text.secondary',
                        fontWeight: formData.category === cat.value ? 600 : 400,
                        '& .MuiChip-icon': {
                          color: formData.category === cat.value ? cat.color : 'grey.400',
                        },
                        '&:hover': {
                          bgcolor: `${cat.color}10`,
                        },
                      }}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Step 2: Timeline */}
        {currentStep === 1 && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h6" fontWeight={600}>
                Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When does this project start and end?
              </Typography>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
                <Grid item xs={12} sm={6}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderColor: formData.startDate ? 'primary.main' : 'grey.200',
                      bgcolor: formData.startDate ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <EventIcon sx={{ color: formData.startDate ? 'primary.main' : 'grey.400' }} />
                      <Typography variant="subtitle2" color={formData.startDate ? 'primary.main' : 'text.secondary'}>
                        Start Date
                      </Typography>
                    </Box>
                    <DatePicker
                      value={formData.startDate ? dayjs(formData.startDate) : null}
                      onChange={(newValue) => {
                        const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData((prev) => ({ ...prev, startDate: dateStr }));
                        if (errors.startDate) {
                          setErrors((prev) => ({ ...prev, startDate: '' }));
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: Boolean(errors.startDate),
                          helperText: errors.startDate,
                          placeholder: 'Select start date',
                        },
                      }}
                    />
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      borderColor: formData.dueDate ? 'warning.main' : 'grey.200',
                      bgcolor: formData.dueDate ? alpha(theme.palette.warning.main, 0.08) : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <EventIcon sx={{ color: formData.dueDate ? 'warning.main' : 'grey.400' }} />
                      <Typography variant="subtitle2" color={formData.dueDate ? 'warning.main' : 'text.secondary'}>
                        Due Date
                      </Typography>
                    </Box>
                    <DatePicker
                      value={formData.dueDate ? dayjs(formData.dueDate) : null}
                      onChange={(newValue) => {
                        const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                        setFormData((prev) => ({ ...prev, dueDate: dateStr }));
                        if (errors.dueDate) {
                          setErrors((prev) => ({ ...prev, dueDate: '' }));
                        }
                      }}
                      minDate={formData.startDate ? dayjs(formData.startDate) : undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: Boolean(errors.dueDate),
                          helperText: errors.dueDate,
                          placeholder: 'Select due date',
                        },
                      }}
                    />
                  </Card>
                </Grid>
              </Grid>
            </LocalizationProvider>

            {projectDuration && (
              <Alert
                severity="info"
                sx={{ mt: 3, borderRadius: 2 }}
                icon={<AccessTimeIcon />}
              >
                Estimated project duration: <strong>{projectDuration}</strong>
              </Alert>
            )}

            {formData.startDate && formData.dueDate && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 3,
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Start</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(formData.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    maxWidth: 200,
                    height: 4,
                    bgcolor: 'primary.main',
                    borderRadius: 2,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: 'warning.main',
                    },
                  }}
                />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Due</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {new Date(formData.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Step 3: Manager Selection */}
        {currentStep === 2 && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h6" fontWeight={600}>
                Project Manager
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Who will lead this project?
              </Typography>
            </Box>

            {managerFetchError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {managerFetchError}
              </Alert>
            )}

            <Autocomplete
              options={availableManagers}
              getOptionLabel={(option) => option.name}
              value={selectedManager || null}
              loading={isLoadingManagers}
              onChange={(event, newValue) => {
                handleManagerSelect(newValue?.id || '');
              }}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: 'primary.main' }}>
                      {getInitials(option.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.department}</Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search manager"
                  placeholder="Type to search..."
                  error={Boolean(errors.managerId)}
                  helperText={errors.managerId}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingManagers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {selectedManager && (
              <Card
                variant="outlined"
                sx={{
                  mt: 3,
                  borderColor: 'success.main',
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 20 }}>
                      {getInitials(selectedManager.name)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight={600} sx={{ fontSize: '1.1rem' }}>{selectedManager.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{selectedManager.department}</Typography>
                      <Typography variant="caption" color="text.secondary">{selectedManager.email}</Typography>
                    </Box>
                    <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Step 4: Priority & Status + Summary */}
        {currentStep === 3 && (
          <Box>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h6" fontWeight={600}>
                Priority & Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set the initial project settings
              </Typography>
            </Box>

            {/* Priority Selection */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Priority Level
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexWrap: 'wrap' }}>
              {PROJECT_PRIORITY_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  icon={<FlagIcon />}
                  onClick={() => setFormData((prev) => ({ ...prev, priority: option.value }))}
                  color={formData.priority === option.value ? option.color : 'default'}
                  variant={formData.priority === option.value ? 'filled' : 'outlined'}
                  sx={{
                    px: 1,
                    height: 36,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              ))}
            </Box>

            {/* Status Selection */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Initial Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 3, sm: 5 }, flexWrap: 'wrap' }}>
              {PROJECT_STATUS_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  onClick={() => setFormData((prev) => ({ ...prev, status: option.value }))}
                  color={formData.status === option.value ? 'primary' : 'default'}
                  variant={formData.status === option.value ? 'filled' : 'outlined'}
                  sx={{
                    height: 36,
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
              ))}
            </Box>

            {/* Summary Card */}
            <Card
              variant="outlined"
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <FolderIcon />
                <Typography variant="subtitle1" fontWeight={600}>
                  Project Summary
                </Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body2" fontWeight={600}>{formData.name}</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Category</Typography>
                    {(() => {
                      const cat = PROJECT_CATEGORY_OPTIONS.find((c) => c.value === formData.category);
                      return cat ? (
                        <Chip
                          label={cat.label}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: 12,
                            bgcolor: `${cat.color}15`,
                            color: cat.color,
                            fontWeight: 500,
                          }}
                        />
                      ) : '-';
                    })()}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Manager</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: 'primary.main' }}>
                        {selectedManager ? getInitials(selectedManager.name) : '?'}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{selectedManager?.name || '-'}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Timeline</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                        {' - '}
                        {formData.dueDate ? new Date(formData.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                      </Typography>
                      {projectDuration && (
                        <Typography variant="caption" color="text.secondary">({projectDuration})</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">Priority</Typography>
                    <Chip
                      label={formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                      size="small"
                      icon={<FlagIcon sx={{ fontSize: 14 }} />}
                      color={formData.priority === 'high' ? 'error' : formData.priority === 'medium' ? 'warning' : 'success'}
                      sx={{ height: 24, fontSize: 12 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={formData.status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 24, fontSize: 12 }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          justifyContent: 'space-between',
          gap: 2,
          mt: { xs: 3, sm: 5 },
          pt: 3,
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={currentStep === 0 ? handleCancel : handleBack}
            fullWidth={isMobile}
            sx={{ minWidth: { sm: 120 } }}
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              fullWidth={isMobile}
              sx={{ minWidth: { sm: 120 } }}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={isSubmitting}
              fullWidth={isMobile}
              sx={{ minWidth: { sm: 160 } }}
            >
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectCreate;
